import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import MainLayouts from "../layout/MainLayouts";
import CourseCard2 from "./Category/CourseCard2";
import client from "../api/client";
import { getUserCountry, getFormattedPrice } from "../utils/currency";
import { getCache, setCache } from "../utils/cache";

export default function Courses() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("live"); // "live" | "online"
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [difficultyFilter, setDifficultyFilter] = useState("all");
  
  const [coursesData, setCoursesData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const country = await getUserCountry();

        const cachedCats = getCache('categories_data');
        const cachedCourses = getCache('courses_data');

        let catData, courseList;

        if (cachedCats && cachedCourses) {
          catData = cachedCats;
          courseList = cachedCourses;
        } else {
          const [res1, res2] = await Promise.all([
            client.get('/v2/categories'),
            client.get('/v2/courses')
          ]);
          catData = res1.data.data || res1.data;
          courseList = res2.data.data || res2.data;
          setCache('categories_data', catData);
          setCache('courses_data', courseList);
        }
        
        // Map Categories
        const normalizedCats = Array.isArray(catData) ? catData : [];
        const mappedCategories = normalizedCats.map(c => ({
          id: c.id.toString(), // or slug
          title: c.name
        }));
        setCategoriesData(mappedCategories);

        // Map Courses
        const normalizedCourses = [...(Array.isArray(courseList) ? courseList : [])].reverse();
        const mappedCourses = normalizedCourses.map(course => ({
          id: course.id,
          title: course.title,
          desc: (course.description || course.what_you_will_learn || '').replace(/<[^>]*>?/gm, '').substring(0, 150) + '...',
          difficulty: course.difficulty_level || "beginner",
          cost: getFormattedPrice(course, country),
          duration: course.duration || "12 Weeks",
          image: course.image_url || "/default-course.jpg",
          format: Number(course.course_type_id) === 1 ? "live" : "online", // 1=live, 2=online
          categoryId: course.category_id ? course.category_id.toString() : "all",
          slug: course.slug
        }));
        setCoursesData(mappedCourses);
      } catch (err) {
        console.error("Error fetching courses page data", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Filter the courses based on all active criteria
  const filteredCourses = useMemo(() => {
    return coursesData.filter((course) => {
      const matchesFormat = course.format === activeTab;
      const matchesCategory = categoryFilter === "all" || course.categoryId === categoryFilter;
      const matchesDifficulty = difficultyFilter === "all" || course.difficulty.toLowerCase() === difficultyFilter.toLowerCase();
      
      return matchesFormat && matchesCategory && matchesDifficulty;
    });
  }, [coursesData, activeTab, categoryFilter, difficultyFilter]);

  return (
    <MainLayouts>
      <div className="bg-[#f4f6f9] min-h-screen">
        
        {/* Hero Section */}
        <div className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 bg-[#0b0f19] overflow-hidden">
          {/* Background Graphic */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b0f19] to-[#1a2333]/30"></div>
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-primary rounded-full blur-[150px] opacity-10"></div>
          
          <div className="relative z-10 max-w-[1560px] mx-auto px-6 text-center">
            <h1 className="text-white text-5xl md:text-7xl font-bold mb-6">
              {t("coursesPage.hero.title")}<span className="text-primary">.</span>
            </h1>
            <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto mb-16">
              {t("coursesPage.hero.subtitle")}
            </p>

            {/* Massive Toggle Switch */}
            <div className="inline-flex p-1.5 bg-white/5 backdrop-blur-md rounded-full border border-white/10 shadow-2xl">
              <button 
                onClick={() => setActiveTab('live')}
                className={`relative px-8 md:px-12 py-4 rounded-full font-bold text-sm md:text-base uppercase tracking-widest transition-all duration-300 ${
                  activeTab === 'live' ? 'text-white shadow-lg' : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                {activeTab === 'live' && (
                  <div className="absolute inset-0 bg-primary rounded-full -z-10 shadow-[0_0_20px_rgba(209,82,66,0.4)]"></div>
                )}
                {t("coursesPage.hero.liveCohorts")}
              </button>
              <button
                onClick={() => setActiveTab('online')}
                className={`relative px-8 md:px-12 py-4 rounded-full font-bold text-sm md:text-base uppercase tracking-widest transition-all duration-300 ${
                  activeTab === 'online' ? 'text-white shadow-lg' : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                {activeTab === 'online' && (
                  <div className="absolute inset-0 bg-primary rounded-full -z-10 shadow-[0_0_20px_rgba(209,82,66,0.4)]"></div>
                )}
                {t("coursesPage.hero.selfPacedOnline")}
              </button>
            </div>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="sticky top-20 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200 shadow-sm transition-all">
          <div className="max-w-[1560px] mx-auto px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm font-bold text-gray-500 uppercase tracking-widest hidden lg:block">
              {t("coursesPage.filters.filterBy")}
            </div>

            <div className="flex w-full md:w-auto items-center gap-4 overflow-x-auto pb-2 md:pb-0 hide-scrollbar">
              {/* Category Filter */}
              <div className="relative shrink-0">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="appearance-none bg-gray-50 border border-gray-200 text-[#0b0f19] text-sm font-bold rounded-full pl-6 pr-12 py-3 outline-none focus:border-primary transition-colors cursor-pointer"
                >
                  <option value="all">{t("coursesPage.filters.allFields")}</option>
                  {categoriesData.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.title}</option>
                  ))}
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </div>
              </div>

              {/* Difficulty Filter */}
              <div className="relative shrink-0">
                <select 
                  value={difficultyFilter}
                  onChange={(e) => setDifficultyFilter(e.target.value)}
                  className="appearance-none bg-gray-50 border border-gray-200 text-[#0b0f19] text-sm font-bold rounded-full pl-6 pr-12 py-3 outline-none focus:border-primary transition-colors cursor-pointer"
                >
                  <option value="all">{t("coursesPage.filters.anyDifficulty")}</option>
                  <option value="beginner">{t("coursesPage.filters.beginner")}</option>
                  <option value="intermediate">{t("coursesPage.filters.intermediate")}</option>
                  <option value="advanced">{t("coursesPage.filters.advanced")}</option>
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Course Grid Area */}
        <div className="py-16 max-w-[1560px] mx-auto px-6">
          <div className="mb-10">
            <h2 className="text-3xl font-bold text-[#0b0f19]">
              {activeTab === 'live' ? t("coursesPage.grid.upcomingLive") : t("coursesPage.grid.selfPacedPrograms")}
              {!loading && filteredCourses.length > 0 && (
                <span className="text-gray-400 text-lg font-medium ml-4">{t("coursesPage.grid.found", { count: filteredCourses.length })}</span>
              )}
            </h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                <div key={n} className="h-full bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100 flex flex-col animate-pulse">
                  <div className="h-[220px] bg-gray-200 w-full"></div>
                  <div className="p-6 md:p-8 flex flex-col flex-grow">
                    <div className="flex gap-3 mb-4">
                      <div className="h-3 w-16 bg-gray-200 rounded"></div>
                      <div className="h-3 w-20 bg-gray-200 rounded"></div>
                    </div>
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
                    <div className="h-6 bg-gray-200 rounded w-1/2 mb-6"></div>
                    <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-2/3 mb-6 flex-grow"></div>
                    <div className="pt-6 mt-auto border-t border-gray-100 flex items-center justify-between">
                      <div className="h-6 w-20 bg-gray-200 rounded"></div>
                      <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredCourses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {filteredCourses.map(course => (
                <CourseCard2 key={course.id} course={course} />
              ))}
            </div>
          ) : activeTab === 'online' && !coursesData.some(c => c.format === 'online') ? (
            <div className="py-32 px-6 flex flex-col items-center justify-center text-center bg-white rounded-[32px] border border-gray-100 shadow-sm">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-6">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-10 h-10"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <h3 className="text-2xl font-bold text-[#0b0f19] mb-2">{t("coursesPage.grid.comingSoonTitle")}</h3>
              <p className="text-gray-500 max-w-md">{t("coursesPage.grid.comingSoonText")}</p>
              <p className="text-gray-600 max-w-2xl mt-6 leading-relaxed">{t("coursesPage.grid.comingSoonDetail")}</p>
              <ul className="mt-8 flex flex-col md:flex-row gap-4 md:gap-8 text-left">
                {[].concat(t("coursesPage.grid.comingSoonPoints", { returnObjects: true }) || []).map((point, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm font-medium text-[#0b0f19] md:max-w-[220px]">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-primary shrink-0"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    {point}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setActiveTab('live')}
                className="mt-8 px-6 py-3 rounded-full bg-primary hover:bg-[#b04233] text-white font-bold text-sm transition-colors"
              >
                {t("coursesPage.grid.viewLiveCourses")}
              </button>
            </div>
          ) : (
            <div className="py-32 flex flex-col items-center justify-center text-center bg-white rounded-[32px] border border-gray-100 shadow-sm">
              <div className="w-20 h-20 rounded-full bg-gray-50 flex items-center justify-center text-gray-300 mb-6">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-10 h-10"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
              </div>
              <h3 className="text-2xl font-bold text-[#0b0f19] mb-2">{t("coursesPage.grid.noMatchTitle")}</h3>
              <p className="text-gray-500 max-w-md">{t("coursesPage.grid.noMatchText")}</p>
              <button
                onClick={() => { setCategoryFilter('all'); setDifficultyFilter('all'); }}
                className="mt-8 px-6 py-3 rounded-full bg-gray-100 hover:bg-gray-200 text-[#0b0f19] font-bold text-sm transition-colors"
              >
                {t("coursesPage.grid.clearFilters")}
              </button>
            </div>
          )}
        </div>
      </div>
    </MainLayouts>
  );
}
